import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Instagram, Linkedin, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Hero({ onCta }) {
  const [social, setSocial] = useState({ instagram: "", linkedin: "", facebook: "" });

  useEffect(() => {
    axios.get(`${API}/site/social`).then((r) => setSocial(r.data || {})).catch(() => { /* ignore */ });
  }, []);

  const socials = [
    { url: social.instagram, Icon: Instagram, label: "Instagram", testId: "hero-social-instagram" },
    { url: social.linkedin, Icon: Linkedin, label: "LinkedIn", testId: "hero-social-linkedin" },
    { url: social.facebook, Icon: Facebook, label: "Facebook", testId: "hero-social-facebook" },
  ].filter((s) => s.url && s.url.trim());

  return (
    <section
      id="top"
      className="relative isolate bg-brand-cream overflow-hidden"
      data-testid="hero-section"
    >
      {/* Soft radial / wash background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1000px 600px at 50% 0%, rgba(255,255,255,0.9), rgba(255,231,208,0) 70%), radial-gradient(700px 500px at 80% 90%, rgba(251,124,29,0.07), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-20 md:pb-24">
        {/* Oversized wordmark */}
        <h1
          className="relative font-poppins font-bold text-brand-navy leading-[0.77] tracking-[-0.04em] text-center"
          data-testid="hero-headline"
        >
          <span className="block text-[16vw] md:text-[14vw] lg:text-[12.5vw]">VANGUARD</span>
          <span className="block text-[16vw] md:text-[14vw] lg:text-[12.5vw]">BEACON</span>
        </h1>

        {/* Tagline under wordmark */}
        <p
          className="relative mt-4 md:mt-2 text-center text-[12px] md:text-[13px] uppercase tracking-[0.45em] text-brand-navy/70"
          data-testid="hero-eyebrow"
        >
          Total Clarity. Without Complexity.
        </p>

        {/* Bottom row — desktop: subhead | CTA | socials in 3 cols. Mobile: CTA on top, then body+socials side-by-side. */}
        <div className="relative mt-16 md:mt-24">
          {/* Mobile-only CTA above the body row */}
          <div className="md:hidden flex flex-col items-center gap-3 mb-10">
            <button
              type="button"
              onClick={onCta}
              aria-label="Scroll to enquiry"
              className="group h-12 w-12 rounded-full border border-brand-navy/40 flex items-center justify-center hover:bg-brand-navy hover:border-brand-navy transition-colors"
              data-testid="hero-cta-primary-mobile"
            >
              <ChevronDown size={18} className="text-brand-navy group-hover:text-brand-cream transition-colors" />
            </button>
            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-navy/50">
              Book a review
            </span>
          </div>

          {/* Mobile body + socials row */}
          <div className="md:hidden flex items-end justify-between gap-6">
            <p
              className="flex-1 text-[15px] text-brand-slate/80 leading-relaxed text-left"
              data-testid="hero-subhead-mobile"
            >
              An AI-powered operations platform that makes compliance effortless.
            </p>
            {socials.length > 0 && (
              <div className="flex flex-col items-end gap-4 text-brand-navy/60 shrink-0">
                {socials.map((s) => <SocialIcon key={`m-${s.label}`} {...s} />)}
              </div>
            )}
          </div>

          {/* Desktop 3-col layout (unchanged behaviour) */}
          <div className="hidden md:grid md:grid-cols-3 items-end gap-10">
            <p
              className="max-w-xs text-[15px] md:text-[17px] text-brand-slate/80 leading-relaxed"
              data-testid="hero-subhead"
            >
              An AI-powered operations platform that makes compliance effortless.
            </p>

            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={onCta}
                aria-label="Scroll to enquiry"
                className="group h-12 w-12 rounded-full border border-brand-navy/40 flex items-center justify-center hover:bg-brand-navy hover:border-brand-navy transition-colors"
                data-testid="hero-cta-primary"
              >
                <ChevronDown
                  size={18}
                  className="text-brand-navy group-hover:text-brand-cream transition-colors"
                />
              </button>
              <span className="text-[10px] uppercase tracking-[0.3em] text-brand-navy/50">
                Book a review
              </span>
            </div>

            <div className="flex md:justify-end items-center gap-5 text-brand-navy/60">
              {socials.length > 0 ? (
                socials.map((s) => <SocialIcon key={`d-${s.label}`} {...s} />)
              ) : (
                <span className="sr-only">Social links not configured</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialIcon({ url, Icon, label, testId }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="hover:text-brand-orange transition-colors"
      data-testid={testId}
    >
      <Icon size={18} strokeWidth={1.5} />
    </a>
  );
}
